/**
 * Appends an opacity (alpha) channel to a hexadecimal color string.
 * Converts a decimal opacity value (0-1) to a 2-digit hex string and appends it.
 *
 * @param color - Hexadecimal formatted color string (e.g., "#3F51B5" or "#fff")
 * @param percentage - Decimal opacity value between 0 and 1 (e.g., 0.15 for 15% opacity)
 * @returns 8-digit hexadecimal color string with alpha channel (e.g., "#3F51B526")
 *
 * @example
 * ```ts
 * reduceOpacity('#3F51B5', 0.15)
 * // Returns: "#3F51B526" (blue with 15% opacity)
 *
 * reduceOpacity('#fff', 0.5)
 * // Returns: "#fff80" (white with 50% opacity)
 *
 * reduceOpacity('#3F51B5', 0.12)
 * // Returns: "#3F51B51f" (blue with 12% opacity)
 * ```
 */
export const reduceOpacity = (color: string, percentage: number): string => {
  // Convert decimal opacity (0-1) to alpha value (0-255) and round to nearest integer
  const alpha = Math.round(percentage * 255);
  // Convert to hexadecimal and pad to 2 digits
  const hexAlpha = alpha.toString(16).padStart(2, '0');
  // Append alpha channel to color string
  return color + hexAlpha;
};

/**
 * Regular expression matching valid hexadecimal color formats.
 * Supports 3-digit shorthand (#RGB) and 6-digit (#RRGGBB) formats, with optional leading #.
 */
const RGB_HEX = /^#?(?:([\da-f]{3})[\da-f]?|([\da-f]{6})(?:[\da-f]{2})?)$/i;

/**
 * Converts a hexadecimal color string to an RGB array.
 * Supports both 3-digit shorthand (#RGB) and 6-digit (#RRGGBB) formats.
 *
 * @param str - Hexadecimal color string (with or without leading #)
 * @returns Array of [R, G, B] values (0-255), or undefined if parsing fails
 *
 * @example
 * ```ts
 * hex2RGB('#3F51B5')
 * // Returns: [63, 81, 181]
 *
 * hex2RGB('#fff')
 * // Returns: [255, 255, 255]
 * ```
 */
const hex2RGB = (str: string) => {
  const [, short, long] = String(str).match(RGB_HEX) || [];

  if (long) {
    const value = Number.parseInt(long, 16);
    return [value >> 16, (value >> 8) & 0xff, value & 0xff];
  } else if (short) {
    return Array.from(short, (s) => Number.parseInt(s, 16)).map((n) => (n << 4) | n);
  }
};

/**
 * Converts a hexadecimal color string to an RGBA color string.
 * Parses the hex color and applies the specified transparency (alpha) value.
 *
 * @param hex - Hexadecimal color string (e.g., "#3F51B5" or "#fff")
 * @param transparency - Alpha value between 0 (fully transparent) and 1 (fully opaque)
 * @returns RGBA color string (e.g., "rgba(63, 81, 181, 0.5)"), or original hex if parsing fails
 *
 * @example
 * ```ts
 * getRgba('#3F51B5', 0.5)
 * // Returns: "rgba(63, 81, 181, 0.5)"
 *
 * getRgba('#fff', 0)
 * // Returns: "rgba(255, 255, 255, 0)" (fully transparent)
 *
 * getRgba('invalid', 0.5)
 * // Returns: "invalid" (returns original hex on parse failure)
 * ```
 */
export const getRgba = (hex: string, transparency: number) => {
  // If parameters are not defined return original hex
  // Use == null to check for both null and undefined, but allow 0 (fully transparent)
  if (!hex || transparency == null) {
    return hex;
  }

  const rgb = hex2RGB(hex);
  if (!rgb) {
    return hex;
  }

  const [r, g, b] = rgb;

  return `rgba(${r}, ${g}, ${b}, ${transparency})`;
};
